import React, { useState, useRef, useEffect } from "react";
import { Upload } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface LogoUploaderProps {
  label: string;
  currentUrl?: string;
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  variant: 'light' | 'dark';
  currentLogoText?: string;
  chooseFileText?: string;
}

export const LogoUploader: React.FC<LogoUploaderProps> = ({
  label,
  currentUrl,
  onUpload,
  isUploading,
  variant,
  currentLogoText = "Logo actuel chargé",
  chooseFileText = "Choisir un fichier"
}) => {
  const { t } = useTranslation("settings");
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // L'aperçu local n'existe que le temps de l'envoi ; dès que le serveur
  // renvoie la nouvelle URL, c'est elle qui s'affiche.
  const previewUrl = localPreview ?? currentUrl ?? null;

  // L'ancien code appelait `URL.revokeObjectURL` juste après l'envoi, alors que
  // l'URL était encore la source du <img> : l'aperçu se vidait après un envoi
  // pourtant réussi. On ne libère l'objet qu'au moment où l'on cesse de
  // l'afficher (nouveau fichier, arrivée de l'URL serveur, démontage).
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  // Le serveur a renvoyé l'URL définitive : on peut abandonner l'aperçu local.
  useEffect(() => {
    if (currentUrl) setLocalPreview(null);
  }, [currentUrl]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    setLocalPreview(URL.createObjectURL(file));

    try {
      await onUpload(file);
    } catch (error) {
      // Envoi échoué : on retire l'aperçu pour ne pas laisser croire que le
      // nouveau logo est enregistré.
      console.error('[LogoUploader] upload failed:', error);
      setLocalPreview(null);
    } finally {
      // Permet de re-sélectionner le même fichier après un échec.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        {/* Preview */}
        <div className={cn(
          "w-32 h-32 rounded-lg border-2 flex items-center justify-center overflow-hidden",
          variant === 'light' ? 'bg-background border-border' : 'bg-muted border-border'
        )}>
          {previewUrl ? (
            <img 
              src={previewUrl} 
              alt={label}
              className="max-w-full max-h-full object-contain p-2"
            />
          ) : (
            <Upload className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        
        {/* Upload button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
            disabled={isUploading}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Upload className="h-4 w-4 mr-2" />
            {isUploading ? t('appearance.uploading') : chooseFileText}
          </Button>
          {currentUrl && (
            <p className="text-xs text-muted-foreground mt-2">
              {currentLogoText}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
